import { FormProvider, useForm } from 'react-hook-form';

import { Alert, Flex, VARIANTS, Show } from 'shared';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';

import { CoachingMode } from 'features/participants/config/types';
import {
  SessionsModalDatePicker,
  SessionsModalPhaseSelector,
  SessionsModalSprintSelector,
} from 'features/sessions/components/Modal';
import { CoachSessionsScheduleModalTimeSelector } from 'features/sessions/components/ScheduleModal/TimeSelector';
import { SESSION_PHASE } from 'features/sessions/config';
import { useSessionSchedule } from 'features/sessions/hooks/useSessionSchedule';
import { getDefaultScheduleModalFormData } from 'features/sessions/utils';
import { SprintsType } from 'features/sprint/config/types';

import {
  modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useModalContext,
} from 'shared/components/Modal';
import { ModalButton } from 'shared/components/Modal/ModalButton';
import { Typography } from 'shared/components/Typography';

type CoachSessionScheduleModalContextType = {
  sprints: SprintsType;
  userId: string;
  options?: any;
  previousSessionId: string;
  coachingMode: CoachingMode;
  isMigrationWarningVisible: boolean;
  startAt?: string;
};

export const CoachSessionScheduleModal = () => {
  const {
    sprints,
    userId,
    options,
    previousSessionId,
    coachingMode,
    startAt,
    isMigrationWarningVisible = false,
  }: CoachSessionScheduleModalContextType = useModalContext();

  const formMethods = useForm({
    defaultValues: getDefaultScheduleModalFormData(
      sprints,
      coachingMode,
      startAt
    ),
  });

  const coachingSessionPhase = formMethods.watch('coachingSessionPhase');

  const { handleSessionSchedule, isLoading, isSessionDateTimeOverlapError } =
    useSessionSchedule({
      userId,
      options,
      previousSessionId,
      onSuccess: () => {
        modal.close();
      },
    });

  return (
    <FormProvider {...formMethods}>
      <ModalHeader>
        <Typography as="h3" type="body" fontWeight="semibold" color="black">
          <Show
            when={isMigrationWarningVisible}
            fallback={<>Schedule session</>}
          >
            You need to set up this session again
          </Show>
        </Typography>
      </ModalHeader>
      <ModalBody>
        <Flex
          flexDirection="column"
          width="100%"
          alignItems="flex-start"
          gap="var(--spacing03)"
        >
          <Show when={isMigrationWarningVisible}>
            <Alert
              status="warning"
              icon={
                <Icon name={iconNames.warning} width="20px" height="20px" />
              }
            >
              As we're migrating to a new session creation flow, please schedule
              the session and ensure that all the details are correct.
            </Alert>
          </Show>
          <SessionsModalPhaseSelector sprints={sprints} />
          {coachingSessionPhase === SESSION_PHASE.SPRINT && (
            <SessionsModalSprintSelector sprints={sprints} />
          )}

          <SessionsModalDatePicker isWarningVisible={false} />
          <CoachSessionsScheduleModalTimeSelector />
          <Show when={isSessionDateTimeOverlapError}>
            <Alert
              status="warning"
              icon={
                <Icon name={iconNames.warning} width="20px" height="20px" />
              }
            >
              Please select another date and time. The date and time you
              selected overlaps with the next session scheduled for this user.
            </Alert>
          </Show>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <ModalButton variant="outline">Cancel</ModalButton>
        <ModalButton
          color={VARIANTS.PRIMARY}
          onClick={formMethods.handleSubmit(handleSessionSchedule)}
          dataTestid="schedule-session-bnt"
          loading={isLoading}
          shouldClose={false}
        >
          Schedule
        </ModalButton>
      </ModalFooter>
    </FormProvider>
  );
};
